import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';

import { cn } from './cn';

/**
 * A small status pill. Semantic tones only — colour is never picked ad hoc,
 * and never decorates. If a badge is the only thing distinguishing two rows,
 * its text has to carry the meaning on its own too.
 */
export const badgeVariants = cva(
  [
    'inline-flex w-fit shrink-0 items-center gap-[var(--space-1-5)]',
    'rounded-[var(--radius-pill)] border',
    'min-h-[1.375rem] px-[var(--space-2)] py-[var(--space-0-5)]',
    'text-[length:var(--text-xs)] font-[var(--weight-medium)] leading-[var(--leading-snug)]',
    'whitespace-nowrap',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3",
  ],
  {
    variants: {
      tone: {
        neutral:
          'border-[color:var(--border-default)] bg-[var(--surface-subtle)] text-[color:var(--text-tertiary)]',
        brand: 'border-[color:var(--brand-200)] bg-[var(--brand-50)] text-[color:var(--brand-800)]',
        success:
          'border-[color:var(--success-border)] bg-[var(--success-bg)] text-[color:var(--success-fg)]',
        warning:
          'border-[color:var(--warning-border)] bg-[var(--warning-bg)] text-[color:var(--warning-fg)]',
        danger:
          'border-[color:var(--danger-border)] bg-[var(--danger-bg)] text-[color:var(--danger-fg)]',
        info: 'border-[color:var(--info-border)] bg-[var(--info-bg)] text-[color:var(--info-fg)]',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

interface Props {
  tone?: BadgeTone;
  children: ReactNode;
  title?: string;
  className?: string;
}

export function Badge({ tone = 'neutral', children, title, className }: Props): JSX.Element {
  return (
    <span className={cn(badgeVariants({ tone }), className)} title={title}>
      {children}
    </span>
  );
}
